import { effect } from '@preact/signals';
import * as S from './state';
import { t } from '../i18n';

/**
 * Keeps the browser from stealing the game: function keys that open help/find/menus,
 * shortcuts that reload or navigate, mouse back/forward buttons, history navigation and
 * accidental tab closing while a hero is in a realm.
 */

/** Browser UI keys that never help in a game (help, find, address bar, caret mode, menu bar). */
const ALWAYS_BLOCKED = new Set(['F1', 'F2', 'F3', 'F4', 'F6', 'F7', 'F9', 'F10']);
/** Extra keys swallowed while in a realm (outside text fields): scrolling, quick find, menu bar. */
const GAME_BLOCKED = new Set(['F5', 'Tab', 'Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Backspace', 'Quote', 'Slash', 'Home', 'End', 'PageUp', 'PageDown', 'AltLeft', 'AltRight']);
/** Ctrl/Cmd shortcuts swallowed while in a realm: save, print, find, bookmark, source, history, downloads, search, reload, zoom. */
const CTRL_BLOCKED = new Set(['KeyS', 'KeyP', 'KeyF', 'KeyD', 'KeyU', 'KeyG', 'KeyH', 'KeyJ', 'KeyK', 'KeyE', 'KeyL', 'KeyO', 'KeyR', 'Equal', 'Minus', 'Digit0', 'NumpadAdd', 'NumpadSubtract']);

function typing(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
}

export function installBrowserGuards(): void {
  // keyboard: capture phase so the browser default is cancelled before anything else runs;
  // the game's own key handlers still receive the event
  window.addEventListener(
    'keydown',
    (e) => {
      if (ALWAYS_BLOCKED.has(e.code)) {
        e.preventDefault();
        return;
      }
      if (!S.inGame.value) return;
      if (e.altKey && (e.code === 'ArrowLeft' || e.code === 'ArrowRight' || e.code === 'Home')) {
        e.preventDefault();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && CTRL_BLOCKED.has(e.code)) {
        e.preventDefault();
        return;
      }
      if (typing()) return;
      if (GAME_BLOCKED.has(e.code) || e.key === 'Alt') e.preventDefault();
    },
    { capture: true },
  );

  // mouse: back/forward buttons navigate away, middle click autoscrolls, ctrl+wheel zooms
  const swallowNav = (e: MouseEvent) => {
    if (e.button === 3 || e.button === 4) e.preventDefault();
    else if (e.button === 1 && S.inGame.value) e.preventDefault();
  };
  for (const type of ['mousedown', 'mouseup', 'auxclick', 'pointerdown', 'pointerup'] as const) window.addEventListener(type, swallowNav as EventListener, { capture: true });
  window.addEventListener(
    'wheel',
    (e) => {
      if (e.ctrlKey && S.inGame.value) e.preventDefault();
    },
    { passive: false, capture: true },
  );

  // history: while in a realm the Back button lands on a guard entry that we immediately restore
  let armed = false;
  effect(() => {
    if (S.inGame.value && !armed) {
      armed = true;
      history.pushState({ knight: 'game' }, '');
    } else if (!S.inGame.value && armed) {
      armed = false;
      if ((history.state as { knight?: string } | null)?.knight === 'game') history.back();
    }
  });
  window.addEventListener('popstate', () => {
    if (!S.inGame.value) return;
    history.pushState({ knight: 'game' }, '');
    S.showToast(t('toast.backBlocked'));
  });

  // closing or reloading the tab mid-run asks first (the hero is saved either way)
  window.addEventListener('beforeunload', (e) => {
    if (!S.inGame.value) return;
    e.preventDefault();
    e.returnValue = '';
  });
}
